import React, { useEffect, useMemo, useState } from "react";
import ModalHeader from "./modalHeader";
import useCookie from "../../logic/cookie";
import { useApi } from "../../logic/classApi";
import { useChemicalApi } from "../../logic/chemicalApi";
import { showErrorMessage, showOrderCompletionMessage, showWarningMessage } from "../appPane";
import { HOLIDAY_BITS, MESSAGES } from "../../constants/const";
import holidayJp from "@holiday-jp/holiday_jp";
import { convertStr } from "../../logic/util";
import { displayDatetimeString, getInputDateString } from "../../logic/dateUtils";
import { useOrderApi } from "../../logic/orderApi";
import { useDownloadApi } from "../../logic/downloadApi";

// 一括発注上限数
const BATCH_ORDER_MAX = 5;

// 30分刻みの時刻選択肢を生成する（例: ["00:00", "00:30", "01:00", ...]）
const THIRTY_MIN_OPTIONS: string[] = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, "0");
  const m = i % 2 === 0 ? "00" : "30";
  return `${h}:${m}`;
});

// 一括発注モーダル
interface Props {
  onClose: () => void;
}

// 一括発注
interface BatchOrderType {
  chemicalId: number;
  chemicalName: string;
  orderVolume: string;
  chemicalUnit: string;
  orderDate: string;
  orderTime: string;
  orderTimeEnd: string;
  isTimeRange: boolean; // 時間帯指定が選択されているかどうか
  isChecked: boolean;
}

/**
 * 一括発注ポップアップ
 * @param props
 * @returns
 */
export const BatchOrderModal: React.FC<Props> = ({ onClose }) => {
  // 支所コード（Cookieから取得）
  const [cookieData] = useCookie();
  const branchCode = cookieData.branchCode;

  // API呼び出し用hook
  const api = useApi();
  const [chemicalData] = useChemicalApi(api, branchCode);
  const [orderData, orderHandle] = useOrderApi(api, branchCode);
  const [downloadHandle] = useDownloadApi(api, branchCode);

  const chemicals = [...(chemicalData.getValidChemicals?.() ?? [])];

  const vendors = [...(chemicalData.vendors || [])];
  const [selectedVendor, setSelectedVendor] = useState("none");

  const centerDate = new Date();
  const endDate = new Date(centerDate);
  endDate.setDate(centerDate.getDate() + 365);
  const orderEndDate = new Date(endDate);

  const [focusedCellId, setFocusedCellId] = useState("");

  // chemicalごとにvendorId・additionalVendorIdを繋げ、対応するvendorNameを元の順序のままカンマ区切りで連結した表示用文字列を作る
  // additionalVendorIdが[]（または未設定）の場合はvendorIdのみで構成される
  const getCombinedVendorName = (chemical: any) => {
    const vendorIds = [chemical.vendorId, ...(chemical.additionalVendorId || [])].filter(
      (id): id is number => id !== undefined && id !== null
    );

    const names = vendorIds
      .map(id => vendors?.find(vendor => vendor.vendorId === id)?.vendorName)
      .filter((name): name is string => !!name);

    return names.join(", ");
  };

  // 重複判定用のキーを作る（vendorIdをソートして連結することで、組み合わせ（集合）が同じであれば順序に関わらず同一キーになる）
  // additionalVendorIdが[]（または未設定）の場合はvendorIdのみのキーになる
  const getCombinedVendorKey = (chemical: any) => {
    const vendorIds = [chemical.vendorId, ...(chemical.additionalVendorId || [])].filter(
      (id): id is number => id !== undefined && id !== null
    );

    return [...vendorIds].sort((a, b) => a - b).join(",");
  };

  // セレクトボックスに表示する選択肢（chemicalごとの結合済み業者名、組み合わせが同じものは重複削除）
  const vendorOptions = useMemo(() => {
    const seenKeys = new Set<string>();
    const options: { key: string; name: string }[] = [];

    (chemicals || []).forEach(chemical => {
      const key = getCombinedVendorKey(chemical);
      const name = getCombinedVendorName(chemical);

      if (name === "" || seenKeys.has(key)) return;

      seenKeys.add(key);
      options.push({ key, name });
    });

    return options;
  }, [chemicals, vendors]);

  // 選択された業者の組み合わせ（key）で薬剤データをフィルターする
  const filteredChemicals = useMemo(() => {
    if (selectedVendor === "none") return [];

    return chemicals?.filter(chemical => getCombinedVendorKey(chemical) === selectedVendor);
  }, [selectedVendor]);

  const [sendData, setSendData] = useState<BatchOrderType[]>([]);

  useEffect(() => {
    const formattedData: BatchOrderType[] = filteredChemicals.map(chemical => {
      const today = new Date();
      const leadTimeDate = new Date(today);
      leadTimeDate.setDate(
        today.getDate() +
          Number(vendors.find(vendor => chemical.vendorId === vendor.vendorId)?.vendorLeadTime)
      );

      return {
        chemicalId: chemical.chemicalId,
        chemicalName: chemical.chemicalName,
        orderVolume: chemical.chemicalMinOrdervolume.toString(),
        chemicalUnit: chemical.chemicalUnit2 || chemical.chemicalUnit1,
        orderDate: convertStr(leadTimeDate, "YYYY-MM-DD") ?? "",
        orderTime: "",
        orderTimeEnd: "",
        isTimeRange: false,
        isChecked: false,
      };
    });

    setSendData(formattedData);
  }, [filteredChemicals]);

  const handleCellChange = <K extends keyof BatchOrderType>(
    id: number,
    field: K,
    newValue: BatchOrderType[K]
  ) => {
    setSendData(prevData =>
      prevData.map(row => (row.chemicalId === id ? { ...row, [field]: newValue } : row))
    );
  };

  // 全選択のチェックボックスの状態を確認
  const isAllChecked = sendData.length > 0 && sendData.every(row => row.isChecked);

  // 一括チェック
  const handleAllCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextCheckedState = e.target.checked;

    setSendData(prevData => prevData.map(row => ({ ...row, isChecked: nextCheckedState })));
  };

  // セレクトボックスの表示値を判定する
  // isTimeRangeがtrueの場合は時間帯指定、未入力は指定なし、それ以外はAM/PM
  const getTimeSelectValue = (orderTime: string, orderTimeEnd: string, isTimeRange: boolean): string => {
    if (isTimeRange) return "時間帯指定";
    if (orderTime === "AM" || orderTime === "PM") return orderTime;
    return "指定なし";
  };

  // フォーム送信
  const sendForm = async (e: React.FormEvent) => {
    e.preventDefault();

    const isCheckedSendData = sendData.filter(data => data.isChecked);

    // 入力エラー処理
    if (
      isCheckedSendData.length === 0 ||
      isCheckedSendData.some(data => data.orderVolume === "" || data.orderDate === "")
    ) {
      showErrorMessage(MESSAGES.ERROR_INPUT_EMPTY);
      return;
    }

    // 発注薬剤数が規定値上限を超える場合はエラーポップアップを表示
    if (isCheckedSendData.length > BATCH_ORDER_MAX) {
      showErrorMessage(MESSAGES.ERROR_INPUT_BATCH_ORDER_MAX);
      return;
    }

    // 最低発注量を下回っていれば警告ポップアップを表示
    const isWarningMinAmount = isCheckedSendData.some(data => {
      const targetChemical = chemicals.find(chemical => chemical.chemicalId === data.chemicalId);
      const minVolume = targetChemical?.chemicalMinOrdervolume ?? 0;
      return minVolume > Number(data.orderVolume);
    });
    if (isWarningMinAmount) {
      const result = showWarningMessage(MESSAGES.WARNING_INPUT_MIN_AMMOUNT);
      if (!result) return;
    }

    // リードタイムを考慮した日にちより過去であれば警告ポップアップを表示
    const isWarningLeadTime = isCheckedSendData.some(data => {
      const targetChemical = chemicals.find(chemical => chemical.chemicalId === data.chemicalId);
      const vendor = vendors.find(vendor => vendor.vendorId === targetChemical?.vendorId);
      const leadTime = vendor?.vendorLeadTime ?? 0;

      const limitDate = new Date();
      limitDate.setDate(limitDate.getDate() + Number(leadTime));
      limitDate.setHours(0, 0, 0, 0);

      const targetDate = new Date(data.orderDate);

      // 誤判定を防ぐため、時刻を一致させる
      targetDate.setHours(0, 0, 0, 0);

      return targetDate < limitDate;
    });
    if (isWarningLeadTime) {
      const result = await showWarningMessage(MESSAGES.WARNING_INPUT_INVALID_DATE);
      if (!result) return;
    }

    // 搬入対応可能日でなければ警告ポップアップを表示
    const isWarningDeliverySupportDays = isCheckedSendData.some(data => {
      const targetChemical = chemicals.find(chemical => chemical.chemicalId === data.chemicalId);
      const vendor = vendors.find(vendor => vendor.vendorId === targetChemical?.vendorId);
      const supportDays = vendor?.vendorDeliverySupportDays || 0;
      const supportsSaturday = (supportDays & HOLIDAY_BITS.SATURDAY) !== 0;
      const supportsSunday = (supportDays & HOLIDAY_BITS.SUNDAY) !== 0;
      const supportsHoliday = (supportDays & HOLIDAY_BITS.HOLIDAY) !== 0;

      const targetDate = new Date(data.orderDate);
      const day = targetDate.getDay();
      const isSaturday = day === 6;
      const isSunday = day === 0;
      const isHoliday = holidayJp.isHoliday(targetDate);

      if (isSaturday && !supportsSaturday) return true;
      if (isSunday && !supportsSunday) return true;
      if (isHoliday && !supportsHoliday) return true;

      return false;
    });
    if (isWarningDeliverySupportDays) {
      const result = await showWarningMessage(MESSAGES.WARNING_INPUT_INVALID_DAY);
      if (!result) return;
    }

    try {
      // 発注処理
      // 薬剤毎に発注登録を実施
      const resolvedSendData = await Promise.all(
        isCheckedSendData.map(async data => {
          const selectValue = getTimeSelectValue(data.orderTime, data.orderTimeEnd, data.isTimeRange);
          const resolvedOrderTime =
            selectValue === "AM" ? "00:00" :
            selectValue === "PM" ? "12:01" :
            selectValue === "指定なし" ? "00:00" :
            data.orderTime;
          const resolvedOrderTimeEnd =
            selectValue === "AM" ? "12:00" :
            selectValue === "PM" ? "23:59" :
            selectValue === "指定なし" ? "00:00" :
            data.orderTimeEnd;

          await orderHandle.saveOrder(
            data.chemicalId,
            data.orderDate,
            resolvedOrderTime,
            resolvedOrderTimeEnd,
            Number(data.orderVolume)
          );

          // ダウンロード用に、送信データの時刻を確定させたものを返す
          return {
            ...data,
            orderTime: resolvedOrderTime,
            orderTimeEnd: resolvedOrderTimeEnd,
          };
        })
      );

      const orderAddDate =
        displayDatetimeString(new Date().toISOString())[0]
          ?.replace(/\([^)]*\)$/, "")
          .replaceAll("/", "_")
          .replace(/[\\/:*?"<>|]/g, "_") ??
        "";
      const chemicalNames = resolvedSendData
        .map(data => data.chemicalName.replace(/\s+/g, "_"))
        .join("_")
        .replace(/[\\/:*?"<>|]/g, "_");
      const fileName = `発注書_${orderAddDate}_${chemicalNames}.xlsx`;

      await downloadHandle.downloadOrder(
        fileName,
        resolvedSendData.map(data => data.chemicalId),
        resolvedSendData.map(data => data.orderDate),
        resolvedSendData.map(data => data.orderTime),
        resolvedSendData.map(data => data.orderTimeEnd),
        resolvedSendData.map(data => Number(data.orderVolume)),
      );

      const selectButtonResult = await showOrderCompletionMessage(MESSAGES.SUCCESS_POST, {
        showPositiveButton: false,
        showNegativeButton: true,
      });
      if (selectButtonResult) {
        // 発注登録一覧画面に遷移
        window.location.href = "/orderlist";
      } else {
        onClose();
      }
    } catch (e) {
      showErrorMessage(MESSAGES.ERROR_POST);
    }
  };

  return (
    <div className="modal">
      <div className="content">
        <ModalHeader title="一括発注" onClose={onClose} />
        <form onSubmit={sendForm}>
          <label htmlFor="vendor-select">薬剤販売業者：</label>
          <select
            name="vendor"
            id="vendor-select"
            value={selectedVendor}
            onChange={e => setSelectedVendor(e.target.value)}
          >
            <option value="none" hidden disabled>
              選択してください
            </option>
            {vendorOptions.map(option => (
              <option key={option.key} value={option.key}>
                {option.name}
              </option>
            ))}
          </select>

          <div className="grid-table-container">
            <div className="grid-table-header">
              <div className="grid-cell">
                <input
                  type="checkbox"
                  className="large-checkbox"
                  id="selectAll-checkbox"
                  onChange={handleAllCheck}
                  checked={isAllChecked}
                ></input>
                <label className="checkbox-label" htmlFor="selectAll-checkbox">
                  全選択
                </label>
              </div>
              <div className="grid-cell">薬剤</div>
              <div className="grid-cell">納入数量</div>
              <div className="grid-cell">納入予定日</div>
              <div className="grid-cell">納入予定時刻</div>
            </div>

            <div className="grid-table-body">
              {selectedVendor === "none" ? (
                <div className="grid-empty-row">薬剤業者が選択されていません。</div>
              ) : sendData?.length === 0 ? (
                <div className="grid-empty-row">発注可能な薬剤が存在しません。</div>
              ) : (
                sendData?.map(data => {
                  const targetChemical = chemicals.find(
                    chemical => chemical.chemicalId === data.chemicalId
                  );
                  return (
                    <div className="grid-table-row">
                      {/* チェックボックス */}
                      <div className="grid-cell">
                        <input
                          type="checkbox"
                          className="large-checkbox"
                          checked={data.isChecked}
                          onChange={e =>
                            handleCellChange(data.chemicalId, "isChecked", e.target.checked)
                          }
                        ></input>
                      </div>
                      {/* 薬剤 */}
                      <div className="grid-cell">{data.chemicalName}</div>
                      {/* 納入数量 */}
                      <div className="grid-cell grid-cell-amount">
                        <input
                          type="number"
                          value={data.orderVolume}
                          step={"any"}
                          min={0}
                          onChange={e =>
                            handleCellChange(data.chemicalId, "orderVolume", e.target.value)
                          }
                          onFocus={() => setFocusedCellId(data.chemicalId.toString())}
                          onBlur={() => setFocusedCellId("")}
                        />
                        <span> {data.chemicalUnit}</span>
                        {focusedCellId === data.chemicalId.toString() && (
                          <div className="grid-cell-memo">
                            ※1
                            {targetChemical?.chemicalUnit1}
                            あたり
                            {targetChemical?.chemicalUnit2Calc}
                            {targetChemical?.chemicalUnit2}
                          </div>
                        )}
                      </div>
                      {/* 納入予定日 */}
                      <div className="grid-cell">
                        <input
                          type="date"
                          value={data.orderDate}
                          min={getInputDateString(centerDate)}
                          max={getInputDateString(orderEndDate)}
                          onChange={e =>
                            handleCellChange(data.chemicalId, "orderDate", e.target.value)
                          }
                        />
                      </div>
                      {/* 納入予定時刻 */}
                      <div className="grid-cell">
                        <select
                          className="grid-cell-item"
                          value={getTimeSelectValue(data.orderTime, data.orderTimeEnd, data.isTimeRange)}
                          onChange={e => {
                            const val = e.target.value;
                            if (val === "指定なし") {
                              handleCellChange(data.chemicalId, "orderTime", "");
                              handleCellChange(data.chemicalId, "orderTimeEnd", "");
                              handleCellChange(data.chemicalId, "isTimeRange", false);
                            } else if (val === "AM" || val === "PM") {
                              handleCellChange(data.chemicalId, "orderTime", val);
                              handleCellChange(data.chemicalId, "orderTimeEnd", "");
                              handleCellChange(data.chemicalId, "isTimeRange", false);
                            } else {
                              // 時間帯指定に切り替えた場合はフラグを立て、時刻入力はリセット
                              handleCellChange(data.chemicalId, "orderTime", "");
                              handleCellChange(data.chemicalId, "orderTimeEnd", "");
                              handleCellChange(data.chemicalId, "isTimeRange", true);
                            }
                          }}
                        >
                          <option>指定なし</option>
                          <option>AM</option>
                          <option>PM</option>
                          <option>時間帯指定</option>
                        </select>
                        {/* 時間帯指定の場合は開始・終了時刻のselectを表示 */}
                        {getTimeSelectValue(data.orderTime, data.orderTimeEnd, data.isTimeRange) === "時間帯指定" && (
                          <>
                            <select
                              className="grid-cell-item"
                              value={data.orderTime}
                              onChange={e => {
                                handleCellChange(data.chemicalId, "orderTime", e.target.value);
                                // 開始時刻変更時、終了時刻が開始時刻より前になる場合はリセット
                                if (data.orderTimeEnd && data.orderTimeEnd < e.target.value) {
                                  handleCellChange(data.chemicalId, "orderTimeEnd", "");
                                }
                              }}
                            >
                              {THIRTY_MIN_OPTIONS.map(time => (
                                <option key={time} value={time}>{time}</option>
                              ))}
                            </select>
                            <span>〜</span>
                            <select
                              className="grid-cell-item"
                              value={data.orderTimeEnd}
                              onChange={e =>
                                handleCellChange(data.chemicalId, "orderTimeEnd", e.target.value)
                              }
                            >
                              {/* 終了時刻は開始時刻以降の選択肢のみ表示 */}
                              {THIRTY_MIN_OPTIONS.filter(time => !data.orderTime || time >= data.orderTime).map(time => (
                                <option key={time} value={time}>{time}</option>
                              ))}
                            </select>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="parts actions">
            <button onClick={onClose} className="negative">
              キャンセル
            </button>

            <button type="submit" className="positive">
              発注登録
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BatchOrderModal;
